import type { ItemMaterial, ProductPublicDto } from "@sticker-giant/sg-frontend-common";

interface QuantityBounds {
  maxQuantity: number;
  minQuantity: number;
}

function getMaterialQuantityBounds(product: ProductPublicDto, material?: ItemMaterial): QuantityBounds | undefined {
  if (!material) return undefined;

  const materialOption = product.materialOptions.find(option => option.sku === material);
  if (!materialOption) return undefined;

  return {
    minQuantity: materialOption.minQuantity,
    maxQuantity: materialOption.maxQuantity,
  };
}

export function getQuantityBounds(product: ProductPublicDto, material?: ItemMaterial): QuantityBounds {
  return (
    getMaterialQuantityBounds(product, material) ?? {
      minQuantity: product.minCustomQuantity,
      maxQuantity: product.maxCustomQuantity,
    }
  );
}

export function getQuantityOptionsForMaterial(product: ProductPublicDto, material?: ItemMaterial): number[] {
  const { minQuantity, maxQuantity } = getQuantityBounds(product, material);
  const filtered = product.quantityOptions.filter(quantity => quantity >= minQuantity && quantity <= maxQuantity);

  // If product metadata is inconsistent, fall back to the full list instead of returning an empty set.
  return filtered.length > 0 ? filtered : product.quantityOptions;
}

export function isQuantityValid(
  product: ProductPublicDto,
  quantity: number,
  material = product.defaultMaterial,
): boolean {
  const { minQuantity, maxQuantity } = getQuantityBounds(product, material);

  if (quantity < minQuantity || quantity > maxQuantity) return false;
  if (product.allowCustomQuantity) return true;

  return getQuantityOptionsForMaterial(product, material).includes(quantity);
}

export function isSoleQuantityOne(quantityOptions: number[]): boolean {
  return quantityOptions.length === 1 && quantityOptions[0] === 1;
}

export function getStartingQuantity(product: ProductPublicDto, quantity?: number, material?: ItemMaterial): number {
  const resolvedMaterial = material ?? product.defaultMaterial;

  // Custom starting quantity
  if (quantity !== undefined && isQuantityValid(product, quantity, resolvedMaterial)) {
    return quantity;
  }

  // Default starting quantity
  const quantityOptions = getQuantityOptionsForMaterial(product, resolvedMaterial);
  return quantityOptions[0];
}
